/**
 * 打卡与计时接口（学生端为主）。路径见方案 4.4。
 *
 * | 方法 | 路径 |
 * | --- | --- |
 * | GET | `/student/today` |
 * | GET | `/student/checkins?from&to&status` |
 * | POST | `/checkins/:id/start \| pause \| resume \| heartbeat \| finish \| skip \| makeup \| undo` |
 * | POST | `/checkins/:id/restore`（**撤销放弃**：孩子限自己今天、非作业项；家长任意） |
 * | POST | `/checkins/:id/approve \| reject`（**仅家长 owner/editor**，见下） |
 *
 * 三条与计时有关的硬约定（见方案 2.3）：
 *  1. `start` **幂等** —— 重复调用返回已存在的计时会话，前端双击不会产生两条记录。
 *  2. 所有时间戳以**服务端**为准。页面绝不能拿本地 `Date.now()` 累加已用时间。
 *  3. 写操作都带 `Idempotency-Key`，弱网重试不会重复加分。
 *
 * 容错：`/student/today` 未就绪时返回空清单 + notReady 标记，
 * 页面显示"该功能尚未就绪"，不会白屏（见任务说明的降级要求）。
 */
import type { CheckinStatus } from '@fht/shared';
import { api, tryRequest } from './client';
import { checkinStatusOf, pickArray, pickObject, toNumber, toOptionalNumber } from './shape';
import type { Checkin, CheckinTimerResponse, TodayTaskDetail } from './types';

export interface TodayResult {
  localDate: string;
  items: TodayTaskDetail[];
  doneCount: number;
  pointsBalance: number;
  /** 后端返回的界面模式；缺失时页面按年级兜底。 */
  uiMode: string | null;
  /** true = 接口未就绪（404/501），页面应显示"功能尚未就绪"。 */
  notReady: boolean;
}

/** 兼容后端用的两种键名（`checkinId` / `id`）。 */
function normalizeTodayTask(raw: unknown): TodayTaskDetail | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;

  const checkinId =
    typeof record.checkinId === 'string'
      ? record.checkinId
      : typeof record.id === 'string'
        ? record.id
        : '';
  if (!checkinId) return null;

  const status = normalizeStatus(record.status);

  return {
    checkinId,
    nodeId: typeof record.nodeId === 'string' ? record.nodeId : '',
    // 后端可能用 habitName / title 而不是 name
    name:
      (typeof record.name === 'string' && record.name) ||
      (typeof record.habitName === 'string' && record.habitName) ||
      (typeof record.title === 'string' && record.title) ||
      '打卡任务',
    goalName: typeof record.goalName === 'string' ? record.goalName : null,
    status,
    estimatedSeconds: toOptionalNumber(record.estimatedSeconds),
    targetValue: toOptionalNumber(record.targetValue),
    unit: typeof record.unit === 'string' ? record.unit : null,
    // N 的兜底是 1（= 改动前的「每单位得 M 分」）；兜底成 0 会让算分除零
    pointsUnitSize: Math.max(1, toNumber(record.pointsUnitSize, 1)),
    pointsPerUnit: toNumber(record.pointsPerUnit, 0),
    icon: typeof record.icon === 'string' ? record.icon : null,
    habitId: typeof record.habitId === 'string' ? record.habitId : undefined,
    actualSeconds: toNumber(record.actualSeconds, 0),
    startedAt: typeof record.startedAt === 'string' ? record.startedAt : null,
    lastHeartbeatAt: typeof record.lastHeartbeatAt === 'string' ? record.lastHeartbeatAt : null,
    valueDone: toOptionalNumber(record.valueDone),
    points: toOptionalNumber(record.points),
    dailyPointsCap: toOptionalNumber(record.dailyPointsCap),
    createdByType: record.createdByType === 'student' ? 'student' : 'parent',
    createdById: typeof record.createdById === 'string' ? record.createdById : '',
    canMakeup: record.canMakeup === true,
    isMakeup: record.isMakeup === true,
    note: typeof record.note === 'string' ? record.note : '',
  };
}

/**
 * 打卡状态的归一化。
 *
 * ⚠️ 这里**转发** `shape.ts` 的 `checkinStatusOf`，不再自己维护一份白名单：
 * 它用的 `CHECKIN_STATUSES` 与后端枚举同源，包含 `submitted`（待家长验收）。
 * 本地那份白名单漏掉 `submitted` 时，`/student/today` 与 `/checkins` 里
 * 已经提交待验收的条目会被降级成 `pending` —— 孩子会看见"还没开始"，
 * 而这种错**不会报错**，只会让人以为自己的完成丢了。
 */
function normalizeStatus(value: unknown): CheckinStatus {
  return checkinStatusOf(value);
}

export function normalizeCheckin(raw: unknown): Checkin | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;

  return {
    id,
    habitId: typeof record.habitId === 'string' ? record.habitId : undefined,
    studentId: typeof record.studentId === 'string' ? record.studentId : undefined,
    nodeId: typeof record.nodeId === 'string' ? record.nodeId : null,
    name: typeof record.name === 'string' ? record.name : null,
    status: normalizeStatus(record.status),
    dueDate: typeof record.dueDate === 'string' ? record.dueDate : undefined,
    dueAt: typeof record.dueAt === 'string' ? record.dueAt : undefined,
    estimatedSeconds: toOptionalNumber(record.estimatedSeconds),
    // 关键：actualSeconds 只认服务端。缺字段按 0，绝不用本地计时补上
    actualSeconds: toNumber(record.actualSeconds, 0),
    startedAt: typeof record.startedAt === 'string' ? record.startedAt : null,
    finishedAt: typeof record.finishedAt === 'string' ? record.finishedAt : null,
    lastHeartbeatAt: typeof record.lastHeartbeatAt === 'string' ? record.lastHeartbeatAt : null,
    valueDone: toOptionalNumber(record.valueDone),
    note: typeof record.note === 'string' ? record.note : '',
    skipReason: typeof record.skipReason === 'string' ? record.skipReason : '',
    isMakeup: record.isMakeup === true,
    pointsAwarded: record.pointsAwarded === true,
    points: toOptionalNumber(record.points),
  };
}

/**
 * 计时接口响应 → `CheckinTimerResponse`。
 *
 * ## ⚠️ 这里曾有过一个"每 30 秒把计时清零"的 bug（2026-10-13 用户实测）
 *
 * 症状：孩子点「开始」后计时走到 **30 秒**就**跳回 0 重新数**。30 秒正是
 * `HEARTBEAT_INTERVAL_SECONDS`，也就是心跳的周期。
 *
 * 根因就在下面这一段：**心跳响应里没有 `id`**。
 * 服务端 `HeartbeatResult`（见 `checkins.service.ts`）返回的是
 * `{ checkinId, status, lastHeartbeatAt, actualSeconds, serverNow }` ——
 * 平铺的 `checkinId`、**没有 `checkin` 包装**。而旧代码先 `pickObject` 下钻
 * （心跳响应没有 `data` 字段，所以下钻后还是它自己），再拿 `record.checkin ?? data`
 * 交给 `normalizeCheckin()`；而 `normalizeCheckin()` 的准入条件是 `record.id`
 * 必须是字符串 —— 心跳响应里根本没有 `id`，于是它返回 `null`，
 * 最后落到下面那个兜底对象：**`actualSeconds: 0`**。
 *
 * 后果（为什么表现成"从 0 重新数"而不是"停住"）：
 *  `useServerTimer` 的心跳回调拿这个 0 去 `reanchor()` → 锚点变成"现在 = 0 秒"，
 *  下一秒的 tick 就从 0 开始往上加。于是**每 30 秒归零一次**，
 *  而且**服务端累计的 `actual_seconds` 一直是正确的**（它是显示层的问题，不是数据问题）。
 *
 * 修法（两层，缺一不可）：
 *  1. **认平铺的 `checkinId`**：`id` 缺失时用 `checkinId` 兜底（下面的 `checkinIdOf`），
 *     并显式从顶层读 `actualSeconds`。判据与夹具见
 *     `@fht/shared` 的 `REAL_HEARTBEAT_RESPONSE_2026_10_13`。
 *  2. **显示层再加一道"锚点只能前进"的守卫**（`useServerTimer` + shared 的
 *     `timer-clock.ts`）：今后任何一次形状不匹配都不会再让表针倒退。
 *     只修第 1 条的话，下一次字段改名会以同样的方式复发。
 */
function checkinIdOf(record: Record<string, unknown>): string | null {
  if (typeof record.id === 'string' && record.id) return record.id;
  if (typeof record.checkinId === 'string' && record.checkinId) return record.checkinId;
  return null;
}

/**
 * 兼容后端用的两种键名（`id` / `checkinId`）与两种包装（平铺 / `checkin` 包装）。
 *
 * ⚠️ `actualSeconds` **只认服务端**：解不出来就是 `null`，
 * 由调用方决定怎么办 —— 绝不在这里悄悄写成 0（那正是本 bug 的形态）。
 * 见 `normalizeTimerResponse` 末尾对 `null` 的处理。
 */
export function normalizeTimerCheckin(raw: unknown): Checkin | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = checkinIdOf(record);
  if (!id) return null;

  return {
    id,
    habitId: typeof record.habitId === 'string' ? record.habitId : undefined,
    studentId: typeof record.studentId === 'string' ? record.studentId : undefined,
    nodeId: typeof record.nodeId === 'string' ? record.nodeId : null,
    name: typeof record.name === 'string' ? record.name : null,
    status: normalizeStatus(record.status),
    dueDate: typeof record.dueDate === 'string' ? record.dueDate : undefined,
    dueAt: typeof record.dueAt === 'string' ? record.dueAt : undefined,
    estimatedSeconds: toOptionalNumber(record.estimatedSeconds),
    actualSeconds: toOptionalNumber(record.actualSeconds),
    startedAt: typeof record.startedAt === 'string' ? record.startedAt : null,
    finishedAt: typeof record.finishedAt === 'string' ? record.finishedAt : null,
    lastHeartbeatAt: typeof record.lastHeartbeatAt === 'string' ? record.lastHeartbeatAt : null,
    valueDone: toOptionalNumber(record.valueDone),
    note: typeof record.note === 'string' ? record.note : '',
    skipReason: typeof record.skipReason === 'string' ? record.skipReason : '',
    isMakeup: record.isMakeup === true,
    pointsAwarded: record.pointsAwarded === true,
    points: toOptionalNumber(record.points),
  };
}

export function normalizeTimerResponse(raw: unknown, fallbackCheckinId: string): CheckinTimerResponse {
  const data = pickObject(raw, raw);
  const record = (data ?? {}) as Record<string, unknown>;
  // 后端可能把打卡记录直接放顶层（心跳 / start / pause / resume，平铺 `checkinId`），
  // 也可能包一层 `checkin`。**平铺那一支是心跳的真实形状**，别把它弄丢。
  const candidate = record.checkin ?? record.checkin_data ?? data;
  const checkin = normalizeTimerCheckin(candidate) ?? normalizeTimerCheckin(data);
  const serverTime =
    typeof record.serverTime === 'string'
      ? record.serverTime
      : typeof record.now === 'string'
        ? record.now
        : typeof record.serverNow === 'string'
          ? record.serverNow
          : undefined;

  /**
   * ⚠️ 兜底路径：连 `checkinId` 都解不出来（字段全改名了这种）。
   *
   * `actualSeconds` 一律取 `null` 而**不是 0**：`0` 是一句"服务端说没走过时间"的
   * 断言，而这里的事实是"我们没读懂响应"。`useServerTimer` 对 `null` 的处理是
   * "这一拍不重新锚定" —— 宁可少一次同步，也绝不把已经走过的秒数抹掉。
   */
  return {
    checkin: checkin ?? {
      id: fallbackCheckinId,
      status: 'pending',
      estimatedSeconds: null,
      actualSeconds: null,
      startedAt: null,
      finishedAt: null,
      lastHeartbeatAt: null,
      valueDone: null,
    },
    ...(serverTime ? { serverTime } : {}),
  };
}

/** 今日待打卡清单（方案 4.4 `GET /student/today`）。 */
export async function studentToday(studentId: string): Promise<TodayResult> {
  const result = await tryRequest(() => api.get<unknown>('/student/today', { studentId }));
  if (result.state === 'notReady') {
    return { localDate: '', items: [], doneCount: 0, pointsBalance: 0, uiMode: null, notReady: true };
  }

  const record = pickObject(result.data, {}) as Record<string, unknown>;
  const rawItems = pickArray<unknown>(record, 'items', 'tasks', 'groups', 'checkins');
  const items = rawItems
    .map(normalizeTodayTask)
    .filter((item): item is TodayTaskDetail => item !== null);

  return {
    localDate: typeof record.localDate === 'string' ? record.localDate : '',
    items,
    doneCount: toNumber(record.doneCount, items.filter((i) => i.status === 'done').length),
    pointsBalance: toNumber(record.pointsBalance, 0),
    uiMode: typeof record.uiMode === 'string' ? record.uiMode : null,
    notReady: false,
  };
}

/** 打卡日历数据（家长端统计页用）。路径是 `GET /checkins`（后端只有这一条，家长查孩子也走它）。 */
export async function listCheckins(
  studentId: string,
  query: { from?: string; to?: string; status?: CheckinStatus; page?: number; pageSize?: number } = {},
): Promise<Checkin[]> {
  const result = await tryRequest(() =>
    api.get<unknown>('/checkins', { studentId, ...query }),
  );
  if (result.state === 'notReady') return [];
  // ⚠️ 后端 `CheckinListResult` 的键是 **`entries`**（见 checkins.service.ts）。
  //   早先候选只有 `checkins` / `items`，都不匹配 → 打卡列表恒为空。
  return pickArray<unknown>(result.data, 'entries', 'checkins', 'items')
    .map(normalizeCheckin)
    .filter((checkin): checkin is Checkin => checkin !== null);
}

// ---------------------------------------------------------------- 计时控制
//
// 每个写操作都接受 `idempotencyKey`：调用方**一次用户意图生成一次**，
// 重试时复用同一个 key。见 client.ts 的 newIdempotencyKey 注释。

interface WriteOptions {
  idempotencyKey?: string;
}

function withIdempotency(options: WriteOptions | undefined) {
  return options?.idempotencyKey
    ? ({ headers: { 'Idempotency-Key': options.idempotencyKey } } as const)
    : {};
}

/** 开始计时（幂等：重复调用返回已存在的会话，双击不会产生两条记录）。 */
export async function startCheckin(
  checkinId: string,
  estimatedSeconds: number,
  options?: WriteOptions,
): Promise<CheckinTimerResponse> {
  const data = await api.post<unknown>(
    `/checkins/${checkinId}/start`,
    { estimatedSeconds },
    withIdempotency(options),
  );
  return normalizeTimerResponse(data, checkinId);
}

/** 暂停。本地暂停也要打服务端接口（见方案 2.3）。 */
export async function pauseCheckin(
  checkinId: string,
  options?: WriteOptions,
): Promise<CheckinTimerResponse> {
  const data = await api.post<unknown>(`/checkins/${checkinId}/pause`, {}, withIdempotency(options));
  return normalizeTimerResponse(data, checkinId);
}

/** 继续。 */
export async function resumeCheckin(
  checkinId: string,
  options?: WriteOptions,
): Promise<CheckinTimerResponse> {
  const data = await api.post<unknown>(`/checkins/${checkinId}/resume`, {}, withIdempotency(options));
  return normalizeTimerResponse(data, checkinId);
}

/**
 * 心跳：计时中每 30 秒调用一次（见方案 2.3 的自动暂停判定）。
 * 心跳失败**不能影响本地计时显示** —— 调用处以"忽略错误"的方式使用它。
 */
export async function heartbeatCheckin(checkinId: string): Promise<CheckinTimerResponse | null> {
  try {
    const data = await api.post<unknown>(`/checkins/${checkinId}/heartbeat`, {});
    return normalizeTimerResponse(data, checkinId);
  } catch {
    // 断网 / 后端未就绪：静默失败。下次心跳（30 秒后）自然会重试，
    // 期间界面继续按"服务端已确认的秒数 + 服务端时间差"显示，不会错乱。
    return null;
  }
}

/** 完成（可填量化完成值与一句备注）。 */
export async function finishCheckin(
  checkinId: string,
  body: { valueDone: number | null; note: string },
  options?: WriteOptions,
): Promise<CheckinTimerResponse> {
  const data = await api.post<unknown>(`/checkins/${checkinId}/finish`, body, withIdempotency(options));
  return normalizeTimerResponse(data, checkinId);
}

/** 放弃（计为未完成，可填原因）。 */
export async function skipCheckin(
  checkinId: string,
  reason: string,
  options?: WriteOptions,
): Promise<CheckinTimerResponse> {
  const data = await api.post<unknown>(
    `/checkins/${checkinId}/skip`,
    { reason },
    withIdempotency(options),
  );
  return normalizeTimerResponse(data, checkinId);
}

/** 补卡（仅宽限期内，标记 is_makeup，不计入连续打卡）。 */
export async function makeupCheckin(
  checkinId: string,
  body: { valueDone: number | null; note: string } = { valueDone: null, note: '' },
  options?: WriteOptions,
): Promise<CheckinTimerResponse> {
  const data = await api.post<unknown>(`/checkins/${checkinId}/makeup`, body, withIdempotency(options));
  return normalizeTimerResponse(data, checkinId);
}

/**
 * 撤销打卡（**仅家长 owner/editor**，见 shared/permissions.ts 的 canUndoCheckin）。
 * 不物理删除，只写 checkin_event 留痕；按方案 3.4.1 也**不自动扣回积分**。
 */
export async function undoCheckin(checkinId: string, options?: WriteOptions): Promise<void> {
  await api.post(`/checkins/${checkinId}/undo`, {}, withIdempotency(options));
}

/**
 * 「撤销放弃」的结果（`POST /checkins/:id/restore`，见 api 的 `restoreSkippedCheckin`）。
 *
 * 与 `undoCheckin`（家长撤销完成）是**两条不同的路**：这条只接受 `skipped → pending`，
 * 孩子本人也能调（限自己今天、限非作业项），是"误点放弃"的回程。
 */
export interface RestoreCheckinResult {
  checkinId: string;
  status: CheckinStatus;
  /** 恢复后保留的"这次真实投入时长"（恢复不改历史事实）。 */
  actualSeconds: number;
  /** 谁退回来的：孩子自己（仅当天）还是家长（任意）。 */
  restoredBy: 'student-self' | 'parent';
  /** 被冲正的原流水（没入过账 = null，放弃一般不产生积分）。 */
  reversedLedgerId: string | null;
  reversedDelta: number;
  reversalEntryId: string | null;
  alreadyReversed: boolean;
  balance: number;
  /** true = 本来就是 `pending`（连点两次不会写第二条事件）。 */
  idempotent: boolean;
}

/**
 * 撤销放弃：把孩子误点的「放弃」恢复成未完成（`skipped → pending`）。
 *
 * ⛔ 不要用 `undoCheckin` 代替它：那条是**家长撤销已完成**的记录，
 *    孩子调用会被 403（`canUndoCheckin` 只认家长 owner/editor）。
 * 权限（服务端为准，前端只是照着隐藏按钮）：
 *  - 学生本人：**只能**恢复自己**今天**、**非作业项**的放弃；
 *  - 家长 owner/editor：任何一天、任何类型；
 *  - 家长 viewer：⛔ 403。
 */
export async function restoreCheckin(
  checkinId: string,
  body: { reason?: string } = {},
  options?: WriteOptions,
): Promise<RestoreCheckinResult> {
  const data = await api.post<unknown>(
    `/checkins/${checkinId}/restore`,
    { reason: body.reason ?? '' },
    withIdempotency(options),
  );
  return normalizeRestoreResult(data, checkinId);
}

/**
 * 把 `POST /checkins/:id/restore` 的响应解成前端模型。
 *
 * 单独 export 是为了**能被单测直接喂响应**（见
 * `lib/api/__tests__/checkins.test.ts`）—— 这个仓库在"解码器只测自己想象的形状"
 * 这件事上摔过四次（计时归零、共享人名空白、听写列表恒空……），所以凡是响应解码
 * 都留一个可测的纯函数。
 *
 * 容错：字段缺失一律给**诚实的中性值**（`reversedDelta: 0` 表示"没有退分"，
 * `idempotent: false` 表示"这次真的做了事"），绝不凭猜测编造一个正数。
 */
export function normalizeRestoreResult(
  raw: unknown,
  fallbackCheckinId: string,
): RestoreCheckinResult {
  const data = pickObject(raw, {}) ?? {};
  const record = data as Record<string, unknown>;
  return {
    checkinId: typeof record.checkinId === 'string' ? record.checkinId : fallbackCheckinId,
    status: normalizeStatus(record.status),
    actualSeconds: toNumber(record.actualSeconds, 0),
    restoredBy: record.restoredBy === 'student-self' ? 'student-self' : 'parent',
    reversedLedgerId:
      typeof record.reversedLedgerId === 'string' ? record.reversedLedgerId : null,
    reversedDelta: toNumber(record.reversedDelta, 0),
    reversalEntryId:
      typeof record.reversalEntryId === 'string' ? record.reversalEntryId : null,
    alreadyReversed: record.alreadyReversed === true,
    balance: toNumber(record.balance, 0),
    idempotent: record.idempotent === true,
  };
}

// ---------------------------------------------------------------- 作业验收（仅家长）

/**
 * 验收结果（`approve` / `reject` 的响应，见 `checkins.service.ts` 的
 * `ApproveResult` / `RejectResult`）。
 *
 * 两个端点共用同一个形状面（这是后端刻意的设计，见契约 4.4）：
 *  - `approve`：`status` 变成 `done`，`pointsAwarded` 是**本次真正入账的分**
 *    （已通过过的重复点击带 `idempotent: true`，分不会重复加）；
 *  - `reject`：`status` 回到 `pending`，`pointsAwarded` 恒为 0，`reason` 是家长填的原因。
 *
 * ⚠️ `idempotent` 只有 `approve` 会返回；`reject` 刻意**不做幂等**
 * （回到 `pending` 后第二次打回会被后端以 409 拒绝 —— 这是明确结果，
 * 好过"静默成功"让家长以为打回生效了两次）。
 */
export interface CheckinApprovalResult {
  checkinId: string;
  status: CheckinStatus;
  /** 验收后恒为 false（已经不欠验收了）。 */
  pendingApproval: boolean;
  actualSeconds: number;
  pointsAwarded: number;
  ledgerEntryId: string | null;
  balance: number;
  /** 打回原因（空字符串 = 家长没填）；`approve` 时不存在。 */
  reason?: string;
  /** true = 这条早就通过过了，本次没有重复加分；`approve` 时才有。 */
  idempotent?: boolean;
}

function normalizeApproval(raw: unknown, fallbackCheckinId: string): CheckinApprovalResult {
  const data = pickObject(raw, raw);
  const record = (data ?? {}) as Record<string, unknown>;
  // 后端把结果直接摊在顶层（`{ ok:true, data:{ checkinId, status, … } }`），
  // 但也容忍包一层 `checkin` 的旧形状（与 normalizeTimerResponse 同一套容错写法）。
  const candidate = pickObject(record.checkin ?? data, data) as Record<string, unknown>;
  const id =
    (typeof candidate.checkinId === 'string' && candidate.checkinId) ||
    (typeof candidate.id === 'string' && candidate.id) ||
    fallbackCheckinId;

  return {
    checkinId: id,
    status: normalizeStatus(candidate.status),
    pendingApproval: candidate.pendingApproval === true,
    actualSeconds: toNumber(candidate.actualSeconds, 0),
    pointsAwarded: toNumber(candidate.pointsAwarded, 0),
    ledgerEntryId:
      typeof candidate.ledgerEntryId === 'string' ? candidate.ledgerEntryId : null,
    balance: toNumber(candidate.balance, 0),
    ...(typeof candidate.reason === 'string' ? { reason: candidate.reason } : {}),
    ...(candidate.idempotent === true ? { idempotent: true } : {}),
  };
}

/**
 * 家长验收通过（`submitted → done`，**此刻才计分**）。
 *
 * ⛔ **仅家长 owner/editor**：门禁是 `canApproveCheckin`，学生调用一律 403。
 * 因此学生端界面**绝不能**渲染这个按钮（我们不靠"点了会失败"来保护）。
 *
 * 幂等由服务端保证（同一 `checkin:<id>` 幂等键）：家长连点两次不会重复加分。
 * 这里仍然带上 `Idempotency-Key`，与其它写操作保持同一套弱网重试语义。
 */
export async function approveCheckin(
  checkinId: string,
  options?: WriteOptions,
): Promise<CheckinApprovalResult> {
  const data = await api.post<unknown>(
    `/checkins/${checkinId}/approve`,
    {},
    withIdempotency(options),
  );
  return normalizeApproval(data, checkinId);
}

/**
 * 家长打回重做（`submitted → pending`，**不计分**）。
 *
 * `reason` 可选：填了会同时写进 `checkin_event.meta`（不可变留档）与
 * `checkin.note`（孩子端卡片上直接能看到"要改什么"）；留空**不动**孩子的原备注。
 */
export async function rejectCheckin(
  checkinId: string,
  reason: string,
  options?: WriteOptions,
): Promise<CheckinApprovalResult> {
  const data = await api.post<unknown>(
    `/checkins/${checkinId}/reject`,
    { reason },
    withIdempotency(options),
  );
  return normalizeApproval(data, checkinId);
}
